import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../ui/button';
import { X, Check, RotateCcw } from 'lucide-react';

/**
 * ImageCropper - A modal component for cropping profile images
 * Uses react-image-crop for the cropping functionality
 */
const ImageCropper = ({ 
  imageSrc, 
  onCropComplete, 
  onCancel,
  aspectRatio = 1, // Default 1:1 for profile pictures
  circularCrop = false
}) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = useRef(null);

  // Center the crop when image loads
  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    // Create a centered crop with the aspect ratio
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspectRatio,
        width,
        height
      ),
      width,
      height
    );
    
    setCrop(crop);
  }, [aspectRatio]);

  // Generate the cropped image
  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    // Calculate pixel values from percentage crop
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    // Set canvas size to the cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(null);
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop]);

  const handleSave = async () => {
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      // Create a File object from the blob
      const croppedFile = new File([croppedBlob], 'profile-cropped.jpg', {
        type: 'image/jpeg',
      });
      onCropComplete(croppedFile);
    }
  };

  const handleReset = () => {
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      const newCrop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          aspectRatio,
          width,
          height
        ),
        width,
        height
      );
      setCrop(newCrop);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[60]">
      <div className="w-full max-w-lg bg-blue-950 border border-blue-700 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-900 border-b border-blue-700 p-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Crop Profile Picture</h3>
            <Button 
              onClick={onCancel}
              variant="ghost" 
              size="sm"
              className="text-gray-400 hover:text-white hover:bg-blue-800"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-1">
            Drag to reposition, resize corners to adjust
          </p>
        </div>

        {/* Crop Area */}
        <div className="p-4 flex justify-center bg-gray-900">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio}
            circularCrop={circularCrop}
            className="max-h-[400px]"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
              style={{ maxHeight: '400px', maxWidth: '100%' }}
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        {/* Actions */}
        <div className="bg-blue-900 border-t border-blue-700 p-4">
          <div className="flex justify-between items-center">
            <Button
              onClick={handleReset}
              variant="outline"
              size="sm"
              className="border-gray-500 text-gray-300 hover:bg-gray-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <div className="flex space-x-2">
              <Button
                onClick={onCancel}
                variant="outline"
                className="border-gray-500 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold"
              >
                <Check className="w-4 h-4 mr-2" />
                Apply Crop
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
